// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function SmartCollageAiTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="Smart Collage Ai"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of “Smart
        Collage Ai” (the “App”). By downloading, installing, accessing, or using
        the App, you agree to be bound by these Terms. If you do not agree, you
        must uninstall the App and discontinue all use. Where required by
        platform rules or local law, additional terms may apply; in case of
        conflict with App Store or Google Play terms, the platform terms may
        prevail to the extent required.
      </p>

      <div className={s.title}>2) Description of the App</div>
      <p>
        The App allows you to create photo collages, story layouts, and grid
        compositions from images stored on your device. You can import several
        photos, choose templates (such as fixed grids or freestyle layouts),
        edit backgrounds and borders, reposition and scale images, and export
        the final design as an image for saving or sharing. Certain modes or
        features (such as “AI Collage” or background removal) may depend on
        third-party services and may be marked as “coming soon” or available
        only to premium subscribers. The App is intended for creative and
        personal use and is not a professional design or printing service.
      </p>

      <div className={s.title}>3) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. Where parental
        consent is required by law, a parent or guardian must approve use. By
        using the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent.
      </p>

      <div className={s.title}>4) User Content and Responsibility</div>
      <p>
        The photos, texts, and other materials you import into or create with
        the App (“User Content”) remain yours or your licensors’. You are solely
        responsible for ensuring that you have all necessary rights and
        permissions to use User Content in the App and to export or share
        resulting collages. This includes permissions from any people appearing
        in the photos and from owners of any artwork, logos, or other protected
        elements.
      </p>
      <p>
        You must not use the App to create, store, or share User Content that is
        illegal in your jurisdiction, infringes intellectual property rights,
        violates privacy or publicity rights, or is otherwise prohibited by
        applicable law or platform rules. You are responsible for how you
        present and distribute exported images on social networks or other
        platforms.
      </p>

      <div className={s.title}>5) License to Process User Content</div>
      <p>
        In order to operate the App and provide features (including template
        application, resizing, and optional AI-powered tools), you grant us and
        our service providers a limited, non-exclusive, worldwide, royalty-free
        license to process User Content solely as necessary to:
      </p>
      <ul>
        <li>import images from your device into the App&apos;s workspace;</li>
        <li>generate previews, layouts, and final exported collages;</li>
        <li>
          apply transformations such as cropping, scaling, filters, and
          background edits;
        </li>
        <li>
          use third-party image APIs where explicitly enabled in the App (for
          example, layout engines or background removal).
        </li>
      </ul>
      <p>
        We do not claim ownership of your User Content. The license ends within
        a reasonable period after you remove the content from the App or
        uninstall it, except where limited retention is required by law, for
        security, or for backup and operational purposes.
      </p>

      <div className={s.title}>6) Image Processing, Export, and Sharing</div>
      <p>
        The App may export final collages as images (for example, JPG or PNG) at
        specified resolutions (such as 1080×1080) to your device gallery or
        share them via other apps. Exported files may be compressed or optimized
        and may not match original source quality or EXIF metadata. Once a
        collage is exported or shared, its further storage and use are governed
        by the terms and privacy policies of the receiving services (such as
        social networks or messaging apps), which we do not control.
      </p>
      <p>
        You are responsible for reviewing the final export before sharing and
        for ensuring that exported content complies with applicable law, third-
        party terms, and the expectations of any persons depicted in the
        images.
      </p>

      <div className={s.title}>7) Local Storage and Data Loss Risk</div>
      <p>
        Collages, drafts, and settings may be stored locally on your device
        (e.g., via local databases or file storage). The App does not require
        registration or operate a mandatory cloud backup service for your
        projects in the MVP version. As a result:
      </p>
      <ul>
        <li>
          if you uninstall the App, reset your device, or lose access to it,
          your locally stored collages and settings may be permanently lost;
        </li>
        <li>
          we cannot recover deleted or lost projects or settings for you;
        </li>
        <li>
          you are responsible for exporting and backing up important work
          outside the App if you wish to keep a copy.
        </li>
      </ul>

      <div className={s.title}>8) Free Tier, Limits, and Premium Features</div>
      <p>
        The App may offer a free tier with limited functionality and a premium
        tier available via subscription. In the free version, you may have
        restrictions such as:
      </p>
      <ul>
        <li>limited number of exported collages;</li>
        <li>inability to use certain “Pro” templates or tools;</li>
        <li>
          watermarks or other visual markers on exported images, if indicated.
        </li>
      </ul>
      <p>
        The premium tier may unlock additional or unlimited exports, access to
        “Pro” templates, extra editing tools, or other features as described on
        the paywall screen. Limits, features, and tier distinctions may be
        updated over time.
      </p>

      <div className={s.title}>9) Subscriptions, Trials, and Billing</div>
      <p>
        Premium features may be offered through auto-renewable subscriptions or
        other in-app purchases (“Subscriptions”), such as weekly plans with a
        free trial. Exact pricing, trial length, and benefits may vary by region
        and are shown within the App and the app stores.
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are governed by the
        policies of the relevant app store.
      </p>

      <div className={s.title}>10) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to premium
        features such as Pro templates or unlimited exports. An internet
        connection may be required to validate or refresh entitlements after
        long offline periods. If platform billing reverses, refunds, or flags a
        transaction as fraudulent, related entitlements may be limited,
        suspended, or revoked.
      </p>

      <div className={s.title}>11) Acceptable Use and Prohibited Content</div>
      <p>
        You agree to use the App only for lawful, personal, and creative
        purposes and not to:
      </p>
      <ul>
        <li>
          create, store, or share content that is illegal, defamatory, or that
          violates the privacy or rights of others;
        </li>
        <li>
          infringe intellectual property rights, including by using images,
          fonts, or assets without required permissions;
        </li>
        <li>
          use the App to harass, threaten, or intimidate individuals or groups;
        </li>
        <li>
          attempt to reverse engineer, decompile, or extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          circumvent technical protections, usage limits, or paywalls separating
          free and premium features;
        </li>
        <li>
          use automated tools or scripts to overload, scrape, or misuse the App
          or any associated services.
        </li>
      </ul>

      <div className={s.title}>12) Templates, Styles, and “AI” Features</div>
      <p>
        The App may provide templates, layouts, styles, and other design
        elements created by us or third-party providers. These elements are
        licensed, not sold, and are intended for use within the App to produce
        collages. You may export final collages incorporating these elements for
        personal or professional presentation, but you may not resell or
        redistribute the templates themselves as standalone assets or as a
        competing template service.
      </p>
      <p>
        If the App offers “AI” or automated layout features, generation may
        depend on third-party APIs. These features are provided on an
        &quot;as-is&quot; basis and may occasionally produce unexpected,
        imperfect, or repetitive results. You remain responsible for reviewing
        any output before sharing or using it commercially.
      </p>

      <div className={s.title}>13) Third-Party Services and APIs</div>
      <p>
        The App may integrate third-party services for image processing,
        background removal, layout optimization, analytics, crash reporting, and
        subscription management (for example, image APIs, RevenueCat, Firebase,
        and similar providers). Where such services are used, relevant data (for
        example, images, metadata, or technical information) may be sent to
        those providers as needed to perform the requested operations.
      </p>
      <p>
        Third-party providers act under their own terms and privacy policies. We
        require them by contract to handle data appropriately and only for
        permitted purposes, but we do not control their independent services.
      </p>

      <div className={s.title}>14) Analytics and Crash Reporting</div>
      <p>
        We may use analytics and crash-reporting tools (for example, Firebase
        Analytics and Crashlytics or similar services) to understand how the App
        is used, monitor performance, and improve stability. Typical events may
        include app launches, photo imports, template selections, exports,
        paywall views, and subscription outcomes. Aggregated or de-identified
        data may be used to guide product decisions. Where required by law, we
        will obtain necessary permissions or provide options to manage analytics
        preferences.
      </p>

      <div className={s.title}>15) No Warranties</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and non-infringement.
        Without limiting the foregoing, we do not warrant that:
      </p>
      <ul>
        <li>
          the App will preserve original image quality or metadata without any
          change or compression;
        </li>
        <li>
          all templates, layouts, or “AI” features will behave as expected on
          every device;
        </li>
        <li>
          the App will operate without interruption, delay, or defects on all
          devices or OS versions.
        </li>
      </ul>

      <div className={s.title}>16) Limitation of Liability</div>
      <p>
        To the extent allowed by law, we are not liable for any indirect,
        incidental, consequential, or punitive damages, loss of profits, loss of
        data (including loss of drafts or exported collages), or business
        interruption arising from or related to your use of or inability to use
        the App, including any issues with image quality, template usage, or
        sharing. Our total aggregate liability to you for direct damages arising
        out of or relating to the App will be limited to the amount you paid for
        Subscriptions during the twelve (12) months prior to the claim, or the
        minimum amount permitted by applicable law if greater. Some
        jurisdictions do not allow certain limitations or exclusions, so parts
        of this section may not apply to you.
      </p>

      <div className={s.title}>17) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your User Content, your sharing of
        collages, your breach of these Terms, or your violation of any rights of
        a third party.
      </p>

      <div className={s.title}>18) Termination</div>
      <p>
        We may suspend or terminate your access to the App or to specific
        features at any time if we reasonably believe you have violated these
        Terms, misused the App, or where required by law, platform rules, or
        security concerns. You may stop using the App at any time by
        uninstalling it and, where applicable, cancelling your Subscription via
        platform settings. Sections that by their nature should survive
        termination (for example, intellectual property, acceptable use,
        limitation of liability, and indemnification) will continue to apply
        after your access ends.
      </p>

      <div className={s.title}>19) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        Smart Collage Ai, which explains how information is collected, used,
        stored, and shared, including images, technical information, and
        analytics. In case of conflict between these Terms and the Privacy
        Policy regarding data processing, the Privacy Policy will guide how we
        handle personal data, while these Terms govern your use of the App and
        its features. You should review the Privacy Policy regularly to
        understand current practices.
      </p>

      <div className={s.title}>20) Changes to the App and to These Terms</div>
      <p>
        We may update the App, add or remove features (including changes to free
        and premium offerings), or modify these Terms from time to time. The
        “Last updated” date at the top of this document indicates when these
        Terms were last revised. Material changes may be highlighted in the App
        or via other reasonable means. By continuing to use the App after
        changes take effect, you accept the updated Terms. If you do not agree
        to the revised Terms, you must stop using the App and uninstall it.
      </p>

      <div className={s.title}>21) Contact Information</div>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing app-specific or
        purchase-related matters.
      </p>
    </TermOfUseLayout>
  );
}
